import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { getUnclaimedQrPayments } from "../../services";
import {
  UnclaimedQrPayment,
  settledAmountOf,
} from "../../types/qr-payment.type";
import { formatNumber } from "../../utils";
import { formatDate } from "../../utils/date";

/**
 * The bill's OWN QR, when it turns out to be holding money.
 *
 * Kept separate from the fetched list rather than merged into it, because it reaches the
 * dialog by a different route and means a different thing. `/qr-payments/unclaimed` answers
 * "confirmed money no bill claims" — this row is claimed, by this very bill, and was closed
 * when the sale moved to another channel. It is the likeliest correct answer on the screen
 * and the operator should not have to recognise it by amount among strangers.
 */
export interface BillOwnQrCandidate {
  uuid: string;
  amount: string;
  paid_amount: string | null;
  paid_at: string | null;
  payer_name: string | null;
}

interface UnclaimedQrPickerProps {
  /** The bill's grand total — what a matching payment must equal. */
  billTotal: number;
  selectedUuid: string;
  onSelect: (uuid: string) => void;
  disabled?: boolean;
  /** This bill's own confirmed-but-cancelled QR, offered first. See the type above. */
  billQr?: BillOwnQrCandidate | null;
}

/** Money is compared in satang so 377.50 and 377.5 are the same payment. */
const sameAmount = (a: number, b: number) =>
  Math.round(a * 100) === Math.round(b * 100);

/**
 * Pick the confirmed QR payment that really settled this bill.
 *
 * Back office cannot raise a QR after the fact — there is no screen for it and the
 * customer is long gone — so "this bill was actually paid by QR" only means anything as
 * "*this* existing payment settled it". The candidates are payments the bank has
 * confirmed that no bill claims: real money in the account with nothing to account for.
 *
 * Matching totals are shown first and alone by default. Two shops really can run up
 * identical amounts on the same day, so a matching total is not proof — but a
 * non-matching one IS disproof, and the API refuses those outright. Showing the rest
 * behind a switch keeps that refusal explainable ("it is there, the amount is wrong")
 * instead of leaving the operator staring at a list their payment is missing from.
 */
export default function UnclaimedQrPicker({
  billTotal,
  selectedUuid,
  onSelect,
  disabled,
  billQr,
}: UnclaimedQrPickerProps) {
  const [rows, setRows] = useState<UnclaimedQrPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const {
        data: { data },
      } = await getUnclaimedQrPayments({ limit: 50 });
      setRows(data?.rows ?? []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Prepended, and never de-duplicated against the fetched rows: the queue only returns
  // payments with `bill_uuid` null, and this one has a bill by definition, so the two lists
  // cannot overlap.
  const candidates = useMemo<BillOwnQrCandidate[]>(
    () => (billQr ? [billQr, ...rows] : rows),
    [billQr, rows],
  );

  const matching = useMemo(
    () => candidates.filter((row) => sameAmount(settledAmountOf(row), billTotal)),
    [candidates, billTotal],
  );

  const visible = showAll ? candidates : matching;

  if (loading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rounded" height={56} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={56} />
      </Box>
    );
  }

  if (failed) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        โหลดรายการ QR ที่ยังไม่มีบิลไม่สำเร็จ กรุณาลองใหม่
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        เลือก QR ที่ลูกค้าจ่าย
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {billQr
          ? "QR ของบิลนี้ที่ถูกปิดไว้ และรายการที่ธนาคารยืนยันแล้วแต่ยังไม่มีบิล"
          : "รายการที่ธนาคารยืนยันแล้วแต่ยังไม่มีบิล"}
      </Typography>

      {candidates.length > 0 && matching.length !== candidates.length && (
        <FormControlLabel
          sx={{ display: "block", mt: 0.5 }}
          control={
            <Switch
              size="small"
              checked={showAll}
              disabled={disabled}
              onChange={(e) => setShowAll(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              แสดงรายการที่ยอดไม่ตรงด้วย ({candidates.length - matching.length})
            </Typography>
          }
        />
      )}

      <Box sx={{ mt: 1 }}>
        {visible.length === 0 && (
          <Alert severity="warning">
            {candidates.length === 0
              ? "ไม่มี QR ที่ธนาคารยืนยันแล้วและยังไม่มีบิล"
              : `ไม่มีรายการที่ยอดตรง ${formatNumber(billTotal)} บาท`}
          </Alert>
        )}

        {visible.map((row) => {
          const amount = settledAmountOf(row);
          const matches = sameAmount(amount, billTotal);
          return (
            <Box
              key={row.uuid}
              onClick={() => !disabled && onSelect(row.uuid)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.5,
                mb: 0.5,
                borderRadius: 1,
                cursor: disabled ? "default" : "pointer",
                border: (theme) =>
                  `1px solid ${
                    selectedUuid === row.uuid
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
              }}
            >
              <Radio
                size="small"
                checked={selectedUuid === row.uuid}
                disabled={disabled}
                onChange={() => onSelect(row.uuid)}
              />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={matches ? "text.primary" : "error.main"}
                  >
                    {formatNumber(amount)} บาท
                  </Typography>
                  {row.uuid === billQr?.uuid && (
                    <Typography
                      variant="caption"
                      color="primary.main"
                      fontWeight={600}
                    >
                      QR ของบิลนี้
                    </Typography>
                  )}
                  {!matches && (
                    <Typography variant="caption" color="error.main">
                      ยอดไม่ตรงกับบิล
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                  noWrap
                >
                  {row.payer_name || "ไม่ทราบชื่อผู้โอน"} ·{" "}
                  {row.paid_at ? formatDate(row.paid_at) : "-"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
